import { useEffect } from 'react';
import { getCanvasWarnings } from '../canvas';
import type { CanvasArchiveKind } from '../exporter';
import type { CanvasDocument } from '../types';

export type CanvasExportSelection = 'composite' | CanvasArchiveKind;

interface CanvasExportDialogProps {
  canvas: CanvasDocument;
  fileName: string;
  exporting: boolean;
  progress: string | null;
  onClose: () => void;
  onExport: (selection: CanvasExportSelection) => void;
}

export function CanvasExportDialog({
  canvas,
  fileName,
  exporting,
  progress,
  onClose,
  onExport,
}: CanvasExportDialogProps) {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !exporting) onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [exporting, onClose]);

  const itemCount = canvas.items.length;
  const itemNoun = canvas.canvasType === 'projection' ? 'projector' : 'LED screen';
  const outsideCount = getCanvasWarnings(canvas).outsideIds.size;

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !exporting) onClose();
      }}
    >
      <section
        className="export-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="canvas-export-title"
      >
        <div className="export-dialog__header">
          <div>
            <span className="eyebrow">CANVAS EXPORT</span>
            <h2 id="canvas-export-title">Choose export package</h2>
          </div>
          <button
            type="button"
            className="tool-button"
            aria-label="Close export dialog"
            disabled={exporting}
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className="export-dialog__summary">
          <strong>{fileName}</strong>
          <span>
            {canvas.resolution.width} × {canvas.resolution.height} px · {itemCount} {itemNoun}
            {itemCount === 1 ? '' : 's'}
          </span>
        </div>

        <div className="export-choice-grid">
          <button
            type="button"
            className="export-choice"
            disabled={exporting}
            autoFocus={itemCount === 0}
            onClick={() => onExport('composite')}
          >
            <span className="export-choice__icon">PNG</span>
            <strong>Composite PNG</strong>
            <small>One flattened image at the full Canvas resolution.</small>
          </button>

          <button
            type="button"
            className="export-choice"
            disabled={exporting || itemCount === 0}
            onClick={() => onExport('outputs')}
          >
            <span className="export-choice__icon">ZIP</span>
            <strong>Separate outputs</strong>
            <small>
              {itemCount || 'No'} native PNG{itemCount === 1 ? '' : 's'} plus placement manifest.
            </small>
          </button>

          <button
            type="button"
            className="export-choice export-choice--primary"
            disabled={exporting || itemCount === 0}
            autoFocus={itemCount > 0}
            onClick={() => onExport('package')}
          >
            <span className="export-choice__icon">ALL</span>
            <strong>Complete package</strong>
            <small>Composite PNG, every native output PNG and manifest in one ZIP.</small>
          </button>

          <button
            type="button"
            className="export-choice export-choice--resolume"
            disabled={exporting || itemCount === 0 || outsideCount > 0}
            onClick={() => onExport('resolume')}
          >
            <span className="export-choice__icon">ARENA</span>
            <strong>Resolume package</strong>
            <small>One Canvas Screen with positioned Slices, plus all PNG files.</small>
          </button>
        </div>

        <p className="export-dialog__note">
          Separate PNGs use each item's native orientation and resolution. Canvas X/Y and rotation
          are recorded in <code>manifest.json</code>.
        </p>

        {outsideCount > 0 && (
          <p className="export-dialog__warning" role="alert">
            Move {outsideCount} outside item{outsideCount === 1 ? '' : 's'} fully inside the Canvas
            to enable Resolume export.
          </p>
        )}

        {progress && (
          <div className="export-dialog__progress" role="status">
            <span className="export-spinner" aria-hidden="true" />
            {progress}
          </div>
        )}
      </section>
    </div>
  );
}
